import { Pencil, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { AdminOrganization, AdminUser } from '../../services/admin'
import { updateAdminUser } from '../../services/admin'
import { validateEmail } from '../../utils/emailValidation'

const inputClass =
  'w-full border border-rule rounded-sm bg-paper px-2 py-1 text-xs text-ink placeholder:text-ink-300 focus:border-ink focus:outline-none focus:ring-1 focus:ring-ink/10'

type EditingField = null | 'name' | 'email'

interface AdminUserAccountFieldsProps {
  user: AdminUser
  organizations: AdminOrganization[]
  isSelf: boolean
  onUpdated: (user: AdminUser) => void
  onToast: (message: string) => void
}

const AdminUserAccountFields: React.FC<AdminUserAccountFieldsProps> = ({
  user,
  organizations,
  isSelf,
  onUpdated,
  onToast,
}) => {
  const [editing, setEditing] = useState<EditingField>(null)
  const [nameDraft, setNameDraft] = useState(user.name)
  const [emailDraft, setEmailDraft] = useState(user.email)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (editing !== 'name') setNameDraft(user.name)
    if (editing !== 'email') setEmailDraft(user.email)
  }, [user.name, user.email, editing])

  async function saveName(e: React.FormEvent) {
    e.preventDefault()
    const name = nameDraft.trim()
    if (!name) return
    setBusy(true)
    try {
      const updated = await updateAdminUser(user.id, { name })
      onUpdated(updated)
      setEditing(null)
      onToast('Navn opdateret.')
    } catch (err: unknown) {
      onToast(err instanceof Error ? err.message : 'Fejl')
    } finally {
      setBusy(false)
    }
  }

  async function saveEmail(e: React.FormEvent) {
    e.preventDefault()
    const check = validateEmail(emailDraft)
    if (!check.ok) {
      onToast(check.message)
      return
    }
    setBusy(true)
    try {
      const updated = await updateAdminUser(user.id, { email: check.email })
      onUpdated(updated)
      setEditing(null)
      onToast('E-mail opdateret.')
    } catch (err: unknown) {
      onToast(err instanceof Error ? err.message : 'Fejl')
    } finally {
      setBusy(false)
    }
  }

  async function handleOrgChange(organizationId: number) {
    if (user.organization.id === organizationId) return
    try {
      const updated = await updateAdminUser(user.id, { organizationId })
      onUpdated(updated)
      onToast('Organisation opdateret.')
    } catch (err: unknown) {
      onToast(err instanceof Error ? err.message : 'Fejl')
    }
  }

  return (
    <dl className="space-y-3 text-xs text-ink">
      <EditableRow
        label="Navn"
        editing={editing === 'name'}
        onEdit={() => setEditing('name')}
        onCancel={() => setEditing(null)}
        display={user.name}
      >
        <form onSubmit={(e) => void saveName(e)} className="flex gap-2">
          <input
            className={inputClass}
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            aria-label="Navn"
            required
          />
          <button
            type="submit"
            disabled={busy || !nameDraft.trim()}
            className="shrink-0 px-2 py-1 bg-ink text-paper rounded-sm disabled:opacity-50"
          >
            Gem
          </button>
        </form>
      </EditableRow>

      <EditableRow
        label="E-mail"
        editing={editing === 'email'}
        onEdit={() => setEditing('email')}
        onCancel={() => setEditing(null)}
        display={user.email}
      >
        <form onSubmit={(e) => void saveEmail(e)} className="flex gap-2">
          <input
            type="email"
            className={inputClass}
            value={emailDraft}
            onChange={(e) => setEmailDraft(e.target.value)}
            aria-label="E-mail"
            required
          />
          <button
            type="submit"
            disabled={busy}
            className="shrink-0 px-2 py-1 bg-ink text-paper rounded-sm disabled:opacity-50"
          >
            Gem
          </button>
        </form>
      </EditableRow>

      <div className="flex justify-between gap-4 items-center">
        <dt className="text-ink-500 shrink-0">Organisation</dt>
        <dd>
          <select
            className="border border-rule rounded-sm bg-paper px-2 py-1 text-xs text-ink focus:border-ink focus:outline-none"
            value={user.organization.id}
            disabled={isSelf}
            aria-label={`Organisation for ${user.name}`}
            onChange={(e) => void handleOrgChange(Number(e.target.value))}
          >
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
        </dd>
      </div>
    </dl>
  )
}

function EditableRow({
  label,
  display,
  editing,
  onEdit,
  onCancel,
  children,
}: {
  label: string
  display: string
  editing: boolean
  onEdit: () => void
  onCancel: () => void
  children: React.ReactNode
}) {
  return (
    <div className="flex justify-between gap-3 items-start">
      <dt className="text-ink-500 shrink-0 pt-0.5">{label}</dt>
      <dd className="min-w-0 flex-1">
        {editing ? (
          children
        ) : (
          <span className="block truncate text-right" title={display}>
            {display}
          </span>
        )}
      </dd>
      <div className="shrink-0">
        {editing ? (
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center text-ink-500 hover:text-ink"
            aria-label={`Annuller redigering af ${label.toLowerCase()}`}
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        ) : (
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex items-center text-ink-500 hover:text-ink"
            aria-label={`Rediger ${label.toLowerCase()}`}
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden />
          </button>
        )}
      </div>
    </div>
  )
}

export default AdminUserAccountFields
